import { AfterViewInit, Component, OnInit } from '@angular/core';
import { IDateRangePickerDashboardItem } from 'src/app/components/date-picker/date-picker.component';
import { IMultipleSelectDashboardItem } from 'src/app/components/multiple-select/multiple-select.component';
import { ChartColorService } from 'src/app/services/chart-color.service';
import { CommonLegendService } from 'src/app/services/common-legend.service';
import { IDashboardItemDataLoaderLink } from 'src/app/services/dashboard-item/IDashboardItemDataLoaderLink';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { DataLoaderFactories } from 'src/app/services/data-loader/data-loader.service';
import { IQueryContext } from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import { DatasourceColumnId } from 'src/app/utils/datasource-sql-map/datasource';
import { EChartsAxisHelper } from 'src/app/utils/echarts/echarts-axis-helper';
import { EChartsTooltipHelper } from 'src/app/utils/echarts/echarts-tooltip-helper';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { SegmentDatasourceColumnDataLoaderFactoryOptions } from '../common/data-loaders/segments';
import { DashboardItemFactories } from '../common/items/common-dashboard-items';
import { StandardDashboardWorker } from '../common/standard-dashboard';
import { createCase1 } from './data-loaders/case_1_monthly';
import { createCase2 } from './data-loaders/case_2_monthly';
import { createCase3 } from './data-loaders/case_3_yearly';
import { createCase4 } from './data-loaders/case_4_revenue_daily_last_60d';
import { createCase5 } from './data-loaders/case_5_single_data_point';
import { createCase6 } from './data-loaders/case_6_weekly';
import { createCase7 } from './data-loaders/case_7_commits_cnt';
import { createCase8 } from './data-loaders/case_8_split_and_no_data';

@Component({
    selector: 'app-dev-echarts',
    templateUrl: './dev-echarts.component.html',
    styleUrls: ['./dev-echarts.component.scss'],
    providers: [DashboardService, QueryContextService, CommonLegendService],
})
export class DevEChartsChomponent
    extends StandardDashboardWorker
    implements OnInit, AfterViewInit
{
    override DASHBOARD_NAME = 'ECharts';
    override DASHBOARD_ID = 'dev-echarts';
    values = Object.values;

    /** GCP Project id used in the dashboard */
    private gcpProjectId: GcpProjectId = 'am-lastarchon-dev';

    /////////////////////////////////////////////////////////////////
    ////
    ////    DATALOADERS
    ////    Some DataLoader can be used in multiple charts.
    ////    That's why there are created here and later referenced
    ////
    /////////////////////////////////////////////////////////////////

    override filterDataLoaders = {
        'dev.echarts.filter.segment': DataLoaderFactories[
            'common.filter.segment'
        ](
            new SegmentDatasourceColumnDataLoaderFactoryOptions(
                'dev.echarts.filter_segments',
                this.gcpProjectId,
                this.queryCache
            )
        ).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    CHART DATALOADERS
    ////
    /////////////////////////////////////////////////////////////////

    override chartDataLoaders = {
        'dev.echarts.case_1': createCase1(),
        'dev.echarts.case_2': createCase2(),
        'dev.echarts.case_3': createCase3(),
        'dev.echarts.case_4': createCase4(),
        'dev.echarts.case_5': createCase5(),
        'dev.echarts.case_6': createCase6(),
        'dev.echarts.case_7': createCase7(),
        'dev.echarts.case_8': createCase8(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    [DASHBOARD ITEM <-> DATALOADER] LINKS
    ////
    /////////////////////////////////////////////////////////////////

    // CHARTS
    case_1: IDashboardItemDataLoaderLink = {
        id: 'lnk.dev.echarts.chart.case_1',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'dev.echarts.chart.case_1',
            dashboardItem: { title: 'Monthly new users' },
            xColumnLabel: 'date',
            yColumnLabel: 'new_users',
            metricName: 'New users',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            tooltip: {
                xLabelFormatterGetter: () =>
                    EChartsAxisHelper.axisLabelDateFormatter,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipIntegerValueFormatter,
            },
        }),
        dataLoaderEmitter: this.chartDataLoaders['dev.echarts.case_1'].emitter,
    };

    case_2: IDashboardItemDataLoaderLink = {
        id: 'lnk.dev.echarts.chart.case_2',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'dev.echarts.chart.case_2',
            dashboardItem: { title: 'Monthly new users' },
            xColumnLabel: 'date',
            yColumnLabel: 'new_users',
            metricName: 'New users',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            tooltip: {
                xLabelFormatterGetter: () =>
                    EChartsAxisHelper.axisLabelDateFormatter,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipIntegerValueFormatter,
            },
        }),
        dataLoaderEmitter: this.chartDataLoaders['dev.echarts.case_2'].emitter,
    };
    case_3_revenue_yearly: IDashboardItemDataLoaderLink = {
        id: 'dev.echarts.chart.case_3_revenue_yearly',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'dev.echarts.chart.case_3_revenue_yearly',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'Revenue',
                subtitle:
                    'Data is agreggated yearly so custom values should be yearly',
            },
            dashboardName: this.DASHBOARD_NAME,
            xColumnLabel: 'date',
            yColumnLabel: 'revenue_net_pln',
            metricName: 'Revenue net PLN',
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (v: any) =>
                        EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            v,
                            context.settings.currency || 'pln'
                        );
                },
            },
        }),
        dataLoaderEmitter: this.chartDataLoaders['dev.echarts.case_3'].emitter,
    };

    case_4_revenue_daily: IDashboardItemDataLoaderLink = {
        id: 'dev.echarts.chart.case_4_revenue_daily',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'dev.echarts.chart.case_4_revenue_daily',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'Revenue',
                subtitle:
                    'Daily data. Timestanp is over a year so xAxis labels and tick can be every month',
            },
            dashboardName: this.DASHBOARD_NAME,
            xColumnLabel: 'date',
            yColumnLabel: 'revenue_net_pln',
            metricName: 'Revenue net PLN',
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (v: any) =>
                        EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            v,
                            context.settings.currency || 'pln'
                        );
                },
            },
        }),
        dataLoaderEmitter: this.chartDataLoaders['dev.echarts.case_4'].emitter,
    };

    case_5_single_data_point: IDashboardItemDataLoaderLink = {
        id: 'dev.echarts.chart.case_5_single_data_point',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'dev.echarts.chart.case_5_single_data_point',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'Revenue',
                subtitle: 'Single data',
            },
            dashboardName: this.DASHBOARD_NAME,
            xColumnLabel: 'date',
            yColumnLabel: 'revenue_net_pln',
            metricName: 'Revenue net PLN',
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (v: any) =>
                        EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            v,
                            context.settings.currency || 'pln'
                        );
                },
            },
        }),
        dataLoaderEmitter: this.chartDataLoaders['dev.echarts.case_5'].emitter,
    };

    case_6_revenue_weekly: IDashboardItemDataLoaderLink = {
        id: 'dev.echarts.chart.case_6_revenue_weekly',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'dev.echarts.chart.case_6_revenue_weekly',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'Revenue',
                subtitle:
                    'Data is aggregated weekly, so custom values should be a full date.',
            },
            dashboardName: this.DASHBOARD_NAME,
            xColumnLabel: 'date',
            yColumnLabel: 'revenue_net_pln',
            metricName: 'Revenue net PLN',
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (v: any) =>
                        EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            v,
                            context.settings.currency || 'pln'
                        );
                },
            },
        }),
        dataLoaderEmitter: this.chartDataLoaders['dev.echarts.case_6'].emitter,
    };

    case_7_cloudsave_commits_count: IDashboardItemDataLoaderLink = {
        id: 'dev.echarts.chart.case_7_commits_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'dev.echarts.chart.case_7_commits_cnt',
            echartsSeriesType: 'bar',
            xColumnLabel: 'date',
            xAxisType: 'category',
            xAxisCategorySubtype: 'datetime',
            // xAxisCategoryDatetimeKey: 'date',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'Commits count',
                subtitle:
                    'The X axis is a category type, so the data should be a full date.',
            },
            dashboardName: this.DASHBOARD_NAME,

            yColumnLabel: 'commits_cnt',
            metricName: 'Commits count',
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
            },
        }),
        dataLoaderEmitter: this.chartDataLoaders['dev.echarts.case_7'].emitter,
    };

    case_8: IDashboardItemDataLoaderLink = {
        id: 'dev.echarts.chart.case_8',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'dev.echarts.chart.case_8',
            dashboardItem: {
                title: 'Case 8 - splits and no data. Proper xAxis ordering',
                subtitle:
                    'Default echarts implementation does not order category axis properly when some dates are missing in data. Must be ordered using xAxis.data',
            },
            echartsSeriesType: 'bar',
            xColumnLabel: 'datetime',
            xAxisType: 'category',
            xAxisCategorySubtype: 'datetime',
            // xAxisCategoryDatetimeKey: 'date',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },

            yColumnLabel: 'cnt_all',
            metricName: 'cnt_all',
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDatetimeTooltipFormatter(
                            label,
                            context
                        );
                },
            },
        }),
        dataLoaderEmitter: this.chartDataLoaders['dev.echarts.case_8'].emitter,
    };

    // FILTERS
    filterDashboardItemLinks: Partial<
        Record<DatasourceColumnId, IDashboardItemDataLoaderLink>
    > = {};

    override async ngOnInit() {
        super.ngOnInit();
        console.log({
            msg: 'DevEChartsChomponent.ngOnInit',
            cancellationToken: this.cancellationToken,
        });
    }

    async ngAfterViewInit() {
        console.log({
            msg: 'DevEChartsChomponent.ngAfterViewInit',
            cancellationToken: this.cancellationToken,
        });

        this.standardDashboardInitialize();
    }

    override getInitialContext(): IQueryContext {
        const initialContext = QueryContextService.INITIAL_CONTEXT;

        const firebaseCountryTierIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'FIRST_GEO_COUNTRY_TIER',
                state: {
                    value: {
                        options: ['Tier 1', 'Tier 2', 'Tier 3'],
                        filterType: 'include',
                    },
                    // filterType: 'include',
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const activityDateIC: IDateRangePickerDashboardItem['datasourceColumn'] =
            {
                id: 'ACTIVITY_DATE',
                state: {
                    value: {
                        preset: 'last 365 days',
                        splitAggregate: 'day',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };

        // set initialContext for AppStore to Google Play
        initialContext.datasourceColumns = [
            // firebaseAppStoreIC,
            // firebaseCountryTierIC,
            activityDateIC,
        ];

        return initialContext;
    }
}
